<template>
    <div>
        <button type="button"
            class="flex-none sm:rounded-full text-sm sm:text-base font-semibold sm:bg-blue-700 sm:hover:bg-blue-600 px-2 sm:px-6 py-1 sm:py-3 text-blue-400 sm:text-blue-100 hover:text-white"
            @click="openModal"
        >
            + {{ $t('New Property') }}
        </button>

        <transition
            enter-active-class="transition ease-out duration-100"
            enter-class="transform opacity-0"
            enter-to-class="transform opacity-100"
            leave-active-class="transition ease-in duration-100"
            leave-class="transform opacity-100"
            leave-to-class="transform opacity-0"
        >
            <div v-show="open"
                class="fixed top-0 left-0 flex items-center justify-center w-full h-full z-top"
                style="background-color: rgba(0, 0, 0, .45)"
            >
                <transition
                    enter-active-class="transition ease-out duration-150"
                    enter-class="transform scale-95"
                    enter-to-class="transform scale-100"
                >
                    <div v-show="open"
                        class="flex items-center justify-center w-full h-full overflow-auto"
                        @click.self="closeModal"
                    >
                        <div v-if="open"
                            @keydown.esc="closeModal"
                            class="relative h-auto p-4 m-2 bg-white rounded shadow-xl w-full md:max-w-lg md:p-6 lg:p-8 md:mx-0"
                        >
                            <div class="absolute p-3 text-2xl text-gray-400 hover:text-gray-600 right-0 top-0 cursor-pointer"
                                @click="closeModal"
                            >
                                <i class="far fa-times-circle"></i>
                            </div>

                            <div class="mb-0">
                                <h2 class="text-xl xs:text-3xl text-center pb-3 xs:pb-6">
                                    {{ $t('New Property') }}
                                </h2>

                                <div class="xs:px-10 mb-2 xs:mb-8">
                                    <div v-if="show_owner_picker"
                                        class="mb-3 xs:mb-5"
                                    >
                                        <label class="block font-semibold text-gray-warm-800 mb-1">
                                            {{ $t('Create transaction for') }}
                                        </label>

                                        <select
                                            class="select-native border border-gray-400 px-2 rounded text-black w-full h-input text-sm"
                                            v-model="for_broker_id"
                                            @change="onOwnerChange"
                                        >
                                            <option v-if="can_add_own_property"
                                                :value="broker.id"
                                            >
                                                {{ broker.name }} ({{ $t('Me') }})
                                            </option>
                                            <option v-for="member in editable_team_members"
                                                :key="member.id"
                                                :value="member.id"
                                            >
                                                {{ member.name }}
                                            </option>
                                        </select>
                                    </div>

                                    <input-text
                                        class="mb-3 xs:mb-5"
                                        input_class="text-sm sm:text-lg w-full"
                                        v-model="name"
                                        label="Client's Name"
                                        placeholder="Name"
                                        required="required"
                                        ref="name"
                                        :enter="submit"
                                    />

                                    <input-text
                                        class="mb-3 xs:mb-5"
                                        input_class="text-sm sm:text-lg w-full"
                                        v-model="email"
                                        label="Client's Email"
                                        placeholder="Email"
                                        required="required"
                                        :enter="submit"
                                    />

                                    <input-text
                                        v-if="has_mls_integration"
                                        class="mb-3 xs:mb-5"
                                        input_class="text-sm sm:text-lg w-full"
                                        v-model="mls_number"
                                        label="MLS #"
                                        placeholder="MLS #"
                                        :enter="submit"
                                        @change="fetchMlsData"
                                    />

                                    <p v-if="mls_loading"
                                        class="flex items-center -mt-2 mb-3 xs:mb-5 text-sm text-blue-600 leading-snug"
                                    >
                                        <spinner class="w-5 h-5 mr-2 flex-none" />
                                        Retrieving property details from MLS ...
                                    </p>

                                    <input-text
                                        class="mb-3 xs:mb-5"
                                        input_class="text-sm sm:text-lg w-full"
                                        v-model="address"
                                        label="Property Address"
                                        placeholder="Address"
                                        required="required"
                                        :enter="submit"
                                    />

                                    <p class="font-semibold text-gray-warm-800 mt-1 mb-2">
                                        {{ $t('Is your client the buyer or the seller?') }}
                                    </p>

                                    <input-radio-boolean
                                        class="pb-2"
                                        name="our_purchase"
                                        true="Buyer"
                                        false="Seller"
                                        v-model="our_purchase"
                                    />

                                    <div v-if="selected_owner_is_commercial">
                                        <p class="font-semibold text-gray-warm-800 mt-3 mb-2">
                                            {{ $t('What type of property is this?') }}
                                        </p>

                                        <input-radio-boolean
                                            class="pb-2"
                                            name="is_commercial"
                                            true="Commercial"
                                            false="Residential"
                                            v-model="is_commercial"
                                        />
                                    </div>

                                    <form-errors :errors="errors" />

                                    <div v-show="status === 'confirm-use-existing'" class="flex flex-1 items-center">
                                        <div class="text-blue-500 text-lg w-8">
                                            <i class="far fa-question-circle mr-1" aria-hidden="true"></i>
                                        </div>

                                        <p class="text-blue-500 leading-tight ml-1">
                                            A user with this email address already exists. <br class="hidden xs:block">Create a new property transaction for them?
                                        </p>
                                    </div>
                                </div>

                                <button type="button"
                                    v-show="status !== 'confirm-use-existing' && status !== 'success'"
                                    class="rounded-full block bg-green-warm-500 hover:bg-green-warm-400 text-white hover:text-white uppercase tracking-wider leading-none px-12 py-4 mx-auto"
                                    @click="submit()"
                                >
                                    {{ $t('Get Started') }}
                                </button>

                                <button type="button"
                                    v-show="status === 'confirm-use-existing' && status !== 'success'"
                                    class="rounded-full block bg-blue-500 hover:bg-blue-400 text-white hover:text-white tracking-wider leading-none px-12 py-4 mx-auto mb-1"
                                    @click="submit()"
                                >
                                    {{ $t('YES, Add a Property') }}
                                </button>

                                <div v-show="status === 'success'"
                                    class="mx-auto py-6 text-2xl text-blue-500 text-center"
                                >
                                    <i class="fas fa-spinner fa-spin"></i>
                                </div>

                                <p v-show="status !== 'confirm-use-existing' && status !== 'success'"
                                    class="text-sm leading-tight font-gray-400 text-center mt-4 xs:mt-8 -mb-2"
                                >
                                   {{ $t('Clicking \'Get Started\' will send an invitation to your Client') }} <br class="hidden xs:block">{{ $t('to start working on a new Property Profile with you.') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
import FormErrors from '../components/form-errors.vue';
import Spinner from '../components/spinner.vue';
import { fetchMlsLookup } from '../mls/fetch-mls-lookup';

export default {
    components: {
        FormErrors,
        Spinner,
    },

    props: {
        has_mls_integration: {},
        broker: {},
        editable_team_members: { default: () => [] },
        can_add_own_property: { default: true },
        default_for_broker_id: { default: null },
    },

    data() {
        return {
            name: '',
            email: '',
            address: '',
            mls_number: '',
            our_purchase: true,
            is_commercial: this.broker.is_commercial,
            for_broker_id: this.defaultOwnerId(),
            errors: [],
            open: false,
            status: null,
            mls_loading: false,
            mls_request_id: 0,
        }
    },

    computed: {
        show_owner_picker() {
            return this.editable_team_members.length > 0;
        },
        selected_owner() {
            if (this.for_broker_id == this.broker.id) {
                return this.broker;
            }

            return this.editable_team_members.find(member => member.id == this.for_broker_id)
                || this.broker;
        },
        selected_owner_is_commercial() {
            return !! this.selected_owner.is_commercial;
        },
    },

    methods: {
        defaultOwnerId() {
            const preferred_id = this.default_for_broker_id;

            if (preferred_id
                && this.editable_team_members.some(member => member.id == preferred_id)
            ) {
                return preferred_id;
            }

            if (this.can_add_own_property) {
                return this.broker.id;
            }

            return this.editable_team_members[0]?.id || this.broker.id;
        },
        onOwnerChange() {
            this.is_commercial = this.selected_owner_is_commercial;
        },
        openModal() {
            this.for_broker_id = this.defaultOwnerId();
            this.is_commercial = this.selected_owner_is_commercial;
            this.open = true;
            this.$nextTick(() => this.$refs.name.focus());
        },
        closeModal() {
            this.open = false;
            this.name = '';
            this.email = '';
            this.address = '';
            this.mls_number = '';
            this.our_purchase = true;
            this.for_broker_id = this.defaultOwnerId();
            this.is_commercial = this.selected_owner_is_commercial;
            this.errors = [];
            this.status = null;
            this.mls_loading = false;
            this.mls_request_id = 0;
        },
        submit() {
            if (this.status !== 'confirm-use-existing') {
                this.status = null;
            }

            axios({
                method: 'post',
                url: '/broker/properties',
                data: {
                    name: this.name,
                    email: this.email,
                    address: this.address,
                    mls_number: this.mls_number,
                    our_purchase: this.our_purchase,
                    is_commercial: this.is_commercial,
                    use_existing: this.status === 'confirm-use-existing',
                    for_broker_id: this.for_broker_id,
                }
            })
                .then(response => {
                    this.errors = [];
                    this.status = 'success';
                    window.location.href = '/broker/properties/' + response.data.property.id + '/dashboard/deal?section=home-office';
                }).catch(error => {
                    console.log(error);

                    if (error.response?.data?.errors) {
                        this.status = 'error';
                        this.errors = Object.values(error.response.data.errors);
                    } else if (error.response?.data?.broker_exists) {
                        this.status = 'error';
                        this.errors = [['A broker with this email address already exists.']];
                    } else if (error.response?.data?.user_exists) {
                        this.status = 'confirm-use-existing';
                    } else if (error.response?.status === 403) {
                        this.status = 'error';
                        this.errors = [['You are not allowed to create a transaction for that team member.']];
                    }
                });
        },
        async fetchMlsData(mls_number) {
            if (! mls_number) {
                return;
            }

            const request_id = ++this.mls_request_id;
            this.mls_loading = true;

            try {
                const response = await fetchMlsLookup(mls_number);

                if (request_id !== this.mls_request_id) {
                    return;
                }

                if (response?.data?.address) {
                    this.address = response.data.address;
                }
            } finally {
                if (request_id === this.mls_request_id) {
                    this.mls_loading = false;
                }
            }
        },
    },
}
</script>
